import React, { FC, ReactNode, Fragment, useEffect } from "react"
import { Action } from "redux"
import { ThunkDispatch } from "redux-thunk"
import { connect } from "react-redux"
import { Modal, ModalBody } from "reactstrap"
import { Route, RouteComponentProps, Redirect } from "react-router-dom"
import { ConnectedReduxProps, ApplicationState } from "../core/types"
import { login } from "./login/store/login-actions"
import { LoginForm, beforeSSOLogin } from "./login/components/LoginForm"
import { LoginPayload } from "./login/store/login-types"
import { User } from "../data/model/user-model"
import { getSSOLoginURL } from "../data/api"

type Role = "user" | "bioinformatician" | "labtech" | "admin"

interface Props {
  component: FC
  children: ReactNode
  user: User | null
  loading: boolean
  errorMessage: string
  error: Error | null
  refresh_token: string | null
  guard: Role
  onLogin(payload: LoginPayload): void
}

type AllProps = Props & RouteComponentProps<object> & ConnectedReduxProps

export function isAuthorized(user: User | null, requiredRole: Role) {
    // This could be changed to use scopes instead of roles once those are provided for the user via WOS2.
    if (!requiredRole) { throw new Error("requiredRole not specified for ProtectedRouteComponent") }
    if (!user) { return false }
    if (requiredRole === "admin") {
        return user.roles.includes("Internal/PbAdmin")
    }
    if (requiredRole === "labtech") {
        return user.roles.includes("Internal/PbLabTech") ||
            user.roles.includes("Internal/PbAdmin")
    }
    if (requiredRole === "bioinformatician") {
        return user.roles.includes("Internal/PbBioinformatician") ||
            user.roles.includes("Internal/PbLabTech") ||
            user.roles.includes("Internal/PbAdmin")
    }
    if (requiredRole === "user") {
        // Any logged in user
        return true
    }
    throw new Error("requested requiredRole " + requiredRole + "is unsupported for ProtectedRouteComponent")
}

const ProtectedRouteComponent: FC<AllProps> = ({
  component,
  children,
  user,
  loading,
  errorMessage,
  refresh_token,
  onLogin,
  guard,
  ...rest
}: AllProps) => {

    // Unauthenticated on an SSO deployment: send the user straight to the IdP.
    // Deep links reach this component directly (AuthRedirect only runs at "/"),
    // so the SSO redirect is handled here as well. /login is not a
    // ProtectedRoute, so it is never bounced by this.
    const ssoLoginUrl = !refresh_token ? getSSOLoginURL() : ""
    useEffect(() => {
        if (ssoLoginUrl) {
            beforeSSOLogin()
            window.location.replace(ssoLoginUrl)
        }
    }, [ssoLoginUrl])

    if ( user && !isAuthorized(user, guard)) {
        return <Redirect to="/bad-perm"/>
    }
    return (
        <Fragment>
            {/* This causes a React error loop sometimes: !user && !refreshToken && <Redirect to="/login"/> */}
            {user && component && <Route {...rest} component={component}/>}
            {user && !component && <Route {...rest}>{children}</Route>}
            {/* Local-login servers (no SSO) still get the modal; SSO deployments
                redirect via the effect above, so no modal flashes there. */}
            {!refresh_token && !ssoLoginUrl &&
            <Modal contentClassName="bg-transparent" size="sm" isOpen>
                <ModalBody className="login-form">
                <LoginForm loading={loading} onLogin={onLogin} errorMessage={errorMessage}/>
                </ModalBody>
            </Modal>
            }
        </Fragment>
    )
}

const mapStateToProps = ({ auth }: ApplicationState) => auth

const mapDispatchToProps = (dispatch: ThunkDispatch<ApplicationState, undefined, Action>) => ({
  onLogin: (payload: LoginPayload) => {
    dispatch(login({ ...payload, disableRedirect: true }))
  }
})

export const ProtectedRoute: any = connect(
  mapStateToProps,
  mapDispatchToProps
)(ProtectedRouteComponent)
